import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import Canvas from './components/Canvas';
import ElementProperties from './components/ElementProperties';
import LayerPanel from './components/LayerPanel';
import { elementRegistry } from './components/ElementRegistry';
import './App.css';
import { debounce } from 'lodash';

// Add console log to check if this file is being loaded
console.log("===== App.jsx is being loaded =====");

function App({ initialElements = [], onElementsChange, eventId, invitationId }) {
  // Replace this verbose logging
  console.log("App component rendering with props:", { 
    initialElementsCount: initialElements?.length, 
    hasOnElementsChange: !!onElementsChange,
    eventId,
    invitationId
  });

  // With this conditional logging that only runs in development
  if (process.env.NODE_ENV === 'development' && !window.__APP_LOGGED__) {
    console.log("App initialized with:", { 
      elementsCount: initialElements?.length, 
      hasCallback: !!onElementsChange,
      eventId,
      invitationId
    });
    window.__APP_LOGGED__ = true;
  }
  
  const [elements, setElements] = useState(initialElements);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // Track whether elements were updated by parent or internally
  const isInternalUpdate = useRef(false);
  
  // Only update from parent when initialElements changes
  useEffect(() => {
    if (initialElements && initialElements.length > 0 && !isInternalUpdate.current) {
      setElements(initialElements);
    }
    // Reset the flag
    isInternalUpdate.current = false;
  }, [initialElements]);
  
  // When elements change, notify parent via callback
  useEffect(() => {
    // Skip the initial render and only notify when elements actually change
    if (initialElements?.length === 0 && elements.length === 0) return;
    if (!onElementsChange) return;

    // Mark this as an internal update to avoid double state updates
    isInternalUpdate.current = true;
    
    // Debounce the callback to avoid too many updates
    // Convert elements to a string and back to ensure deep cloning
    const handleElementsChangeDebounced = debounce(() => {
      // Check if the parent has a non-object callback or the new object structure
      if (typeof onElementsChange === 'function') {
        onElementsChange(JSON.parse(JSON.stringify(elements)));
      } else if (onElementsChange && typeof onElementsChange.call === 'function') {
        onElementsChange.call(JSON.parse(JSON.stringify(elements)));
      }
    }, 300);
    
    handleElementsChangeDebounced();
    
    // Clean up the debounce timer
    return () => {
      handleElementsChangeDebounced.cancel();
    };
  }, [elements, initialElements, onElementsChange]);
  
  // Add state history for undo/redo
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isHistoryChange = useRef(false);
  
  // Update history when elements change
  useEffect(() => {
    if (isHistoryChange.current) {
      isHistoryChange.current = false;
      return;
    }
    
    // Don't add to history if elements are empty or the same as last history entry
    if (elements.length === 0) return;
    
    // Compare with last history entry
    const lastHistoryEntry = history[historyIndex];
    if (lastHistoryEntry && JSON.stringify(lastHistoryEntry) === JSON.stringify(elements)) {
      return; // Skip if no actual changes
    }
    
    console.log("Adding to history, index:", historyIndex, "new length:", historyIndex + 2);
    
    // Add current state to history, removing any future states
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(elements))); // Deep copy to ensure immutability
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [elements, history, historyIndex]);
  
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  
  const handleUndo = () => {
    if (!canUndo) return;
    
    console.log("Undo: moving from index", historyIndex, "to", historyIndex - 1);
    isHistoryChange.current = true;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    
    // Use deep copy to ensure we don't mutate history
    const prevState = JSON.parse(JSON.stringify(history[newIndex]));
    setElements(prevState);
  };
  
  const handleRedo = () => {
    if (!canRedo) return;
    
    console.log("Redo: moving from index", historyIndex, "to", historyIndex + 1);
    isHistoryChange.current = true;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    
    // Use deep copy to ensure we don't mutate history
    const nextState = JSON.parse(JSON.stringify(history[newIndex]));
    setElements(nextState);
  };
  
  const [scrollPosition, setScrollPosition] = useState(0);
  const mainContentRef = useRef(null);
  
  // Add scroll tracker
  useEffect(() => {
    const mainContent = mainContentRef.current;
    if (!mainContent) return;
    
    const handleScroll = () => {
      setScrollPosition(mainContent.scrollTop);
    };
    
    mainContent.addEventListener('scroll', handleScroll);
    return () => {
      mainContent.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const addElement = (type, data = {}) => {
    // Get canvas boundaries
    const canvasElement = document.querySelector('.canvas');
    let canvasRect = { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
    
    if (canvasElement) {
      canvasRect = canvasElement.getBoundingClientRect();
    }
    
    // Default position - ensure it's within canvas boundaries
    const defaultX = Math.min(Math.max(50, data.x || Math.floor(Math.random() * (canvasRect.width - 200))), canvasRect.width - 200);
    const defaultY = Math.min(Math.max(50, data.y || Math.floor(Math.random() * (canvasRect.height - 200))), canvasRect.height - 200);
    
    const newElement = {
      id: `element-${Date.now()}`,
      type,
      position: {
        x: data.x !== undefined ? data.x : defaultX,
        y: data.y !== undefined ? data.y : defaultY
      },
      style: {
        ...elementRegistry[type]?.defaultStyles
      },
      ...data
    };

    // For icon elements, ensure we have the icon data
    if (type === 'icon' && data.icon) {
      newElement.icon = data.icon;
    }

    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };
  
  const updateElement = (id, updates) => {
    setElements(elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  };
  
  const deleteElement = (id) => {
    setElements(elements.filter(el => el.id !== id));
    if (selectedElement === id) setSelectedElement(null);
  };

  const togglePreviewMode = () => {
    console.log("Toggling preview mode from", isPreviewMode, "to", !isPreviewMode);
    setIsPreviewMode(!isPreviewMode);
    
    // When entering preview mode, deselect any element
    if (!isPreviewMode) {
      setSelectedElement(null);
    }
    
    // Force re-render of the canvas in preview mode
    setTimeout(() => {
      const canvasContainer = document.querySelector('.canvas-container');
      if (canvasContainer) {
        // Trigger layout recalculation
        canvasContainer.style.display = 'none';
        setTimeout(() => canvasContainer.style.display = 'block', 5);
      }
    }, 10);
  };
  
  const addCustomElement = (elementData) => {
    setElements([...elements, elementData]);
    setSelectedElement(elementData.id);
  };
  
  // Add this state to track current device view mode
  const [deviceView, setDeviceView] = useState('desktop'); // Options: desktop, mobile
  
  // Add this useEffect to handle the sizing when the component mounts
  useEffect(() => {
    // Adjust layout for being embedded in EditorPage 
    const appContainer = document.querySelector('.app-container');
    if (appContainer && appContainer.closest('.editor-container')) {
      // Apply specific styles when embedded
      appContainer.classList.add('embedded-app');
    }
    
    return () => {
      // Clean up on unmount
      const appContainer = document.querySelector('.app-container');
      if (appContainer) {
        appContainer.classList.remove('embedded-app');
      }
    };
  }, []);
  
  // When elements state changes
  useEffect(() => {
    console.log("Elements in App component:", elements);
  }, [elements]);
  
  // When initialElements prop changes
  useEffect(() => {
    console.log("initialElements received in App:", initialElements);
  }, [initialElements]);
  
  const handleElementUpdate = (updatedElement) => {
    console.log("Element updated:", updatedElement.id, "Type:", updatedElement.type);
    
    // Create a new elements array with the updated element replaced
    const newElements = elements.map(el => 
      el.id === updatedElement.id ? updatedElement : el
    );
    
    // Important: Create a complete deep copy before sending back to parent
    const deepCopyElements = JSON.parse(JSON.stringify(newElements));
    
    // Log before sending to parent
    console.log("Sending updated elements to parent, count:", deepCopyElements.length);
    
    // Call the parent's callback with the new array
    if (typeof onElementsChange === 'function') {
      onElementsChange(deepCopyElements);
    } else if (onElementsChange && typeof onElementsChange.call === 'function') {
      onElementsChange.call(deepCopyElements);
    }
  };
  
  // Add state to track if layer panel is open
  const [isLayerPanelOpen, setIsLayerPanelOpen] = useState(false);
  
  // Helper function to call the parent's callback with elements
  const notifyParentOfChanges = (elements) => {
    if (typeof onElementsChange === 'function') {
      onElementsChange(elements);
    } else if (onElementsChange && typeof onElementsChange.call === 'function') {
      onElementsChange.call(elements);
    }
  };
  
  // Add layer management functions
  const moveElementForward = (id) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    // Find the element with the next highest z-index
    const sortedElements = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const currentIndex = sortedElements.findIndex(el => el.id === id);
    
    // If already at the top, do nothing
    if (currentIndex === sortedElements.length - 1) return;
    
    const nextElement = sortedElements[currentIndex + 1];
    const newZIndex = (nextElement.zIndex || 0) + 1;
    
    // Update the element and notify parent
    const updatedElements = elements.map(el => {
      if (el.id === id) {
        return { ...el, zIndex: newZIndex };
      }
      return el;
    });
    
    setElements(updatedElements);
    if (onElementsChange) {
      notifyParentOfChanges(updatedElements);
    }
  };
  
  const moveElementBackward = (id) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    // Find the element with the next lowest z-index
    const sortedElements = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const currentIndex = sortedElements.findIndex(el => el.id === id);
    
    // If already at the bottom, do nothing
    if (currentIndex === 0) return;
    
    const prevElement = sortedElements[currentIndex - 1];
    const newZIndex = (prevElement.zIndex || 0) - 1;
    
    // Update the element and notify parent
    const updatedElements = elements.map(el => {
      if (el.id === id) {
        return { ...el, zIndex: newZIndex };
      }
      return el;
    });
    
    setElements(updatedElements);
    if (onElementsChange) {
      notifyParentOfChanges(updatedElements);
    }
  };
  
  const bringElementToFront = (id) => {
    // Find the highest z-index
    const highestZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    
    // Update the element and notify parent
    const updatedElements = elements.map(el => {
      if (el.id === id) {
        return { ...el, zIndex: highestZIndex + 1 };
      }
      return el;
    });
    
    setElements(updatedElements);
    if (onElementsChange) {
      notifyParentOfChanges(updatedElements);
    }
  };
  
  const sendElementToBack = (id) => {
    // Find the lowest z-index
    const lowestZIndex = Math.min(...elements.map(el => el.zIndex || 0), 0);
    
    // Update the element and notify parent
    const updatedElements = elements.map(el => {
      if (el.id === id) {
        return { ...el, zIndex: lowestZIndex - 1 };
      }
      return el;
    });
    
    setElements(updatedElements);
    if (onElementsChange) {
      notifyParentOfChanges(updatedElements);
    }
  };
  
  // Add effect for listening to custom event
  useEffect(() => {
    const handleToggleLayerPanel = () => {
      setIsLayerPanelOpen(!isLayerPanelOpen);
    };
    
    window.addEventListener('toggle-layer-panel', handleToggleLayerPanel);
    
    return () => {
      window.removeEventListener('toggle-layer-panel', handleToggleLayerPanel);
    };
  }, [isLayerPanelOpen]);
  
  return (
    <div className="app-container">
      {/* Empty header - will be minimized via CSS */}
      <div className="app-header"></div>
      
      {/* New toolbar with white background */}
      <div className="app-toolbar">
        {/* Page Builder title moved from header to toolbar */}
        <div className="app-title">Page Builder</div>
        
        <div className="toolbar-left">
          {/* Layers button has been moved to the sidebar */}
        </div>
        
        <div className="toolbar-right">
          {/* Undo/Redo buttons */}
          <div className="action-group">
            <button 
              className={`action-btn ${!canUndo ? 'disabled' : ''}`} 
              onClick={handleUndo}
              disabled={!canUndo}
              title="Undo"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 8C9.85 8 7.45 8.99 5.6 10.6L2 7V16H11L7.38 12.38C8.77 11.22 10.54 10.5 12.5 10.5C16.04 10.5 19.05 12.81 20.1 16L22.47 15.22C21.08 11.03 17.15 8 12.5 8Z" fill="currentColor"/>
              </svg>
            </button>
            <button 
              className={`action-btn ${!canRedo ? 'disabled' : ''}`} 
              onClick={handleRedo}
              disabled={!canRedo}
              title="Redo"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8C6.85 8 2.92 11.03 1.54 15.22L3.9 16C4.95 12.81 7.95 10.5 11.5 10.5C13.45 10.5 15.23 11.22 16.62 12.38L13 16H22V7L18.4 10.6Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          
          <div className="action-divider"></div>
          
          {/* Device Preview Selector */}
          <div className="device-preview-selector">
            <button 
              className={`device-btn ${deviceView === 'desktop' ? 'active' : ''}`}
              onClick={() => setDeviceView('desktop')}
              title="Desktop View"
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="18" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M6 15H14" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </button>
            
            <button 
              className={`device-btn ${deviceView === 'mobile' ? 'active' : ''}`}
              onClick={() => {
                // Instead of switching to mobile view, show a notification
                const notification = document.createElement('div');
                notification.className = 'feature-coming-soon-notification';
                notification.textContent = 'Mobile view is coming soon!';
                document.body.appendChild(notification);
                
                // Automatically remove after 3 seconds
                setTimeout(() => {
                  if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                  }
                }, 3000);
                
                // Force desktop view
                setDeviceView('desktop');
              }}
              title="Mobile View (Coming Soon)"
            >
              <svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="10" height="16" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M5 14H7" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </button>
          </div>
          
          <div className="action-divider"></div>
          
          {/* Preview Button - moved from header to toolbar */}
          <button 
            className={`preview-btn ${isPreviewMode ? 'active' : ''}`}
            onClick={togglePreviewMode}
            title={isPreviewMode ? "Exit Preview" : "Preview"}
          >
            {isPreviewMode ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '8px'}}>
                  <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Exit Preview
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '8px'}}>
                  <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2.45825 12C3.73253 7.94288 7.52281 5 12.0004 5C16.4781 5 20.2684 7.94291 21.5426 12C20.2684 16.0571 16.4781 19 12.0005 19C7.52281 19 3.73251 16.0571 2.45825 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Preview
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="app-content">
        <Sidebar
          addElement={addElement}
          isPreviewMode={isPreviewMode}
          setIsPreviewMode={setIsPreviewMode}
        />
        
        {/* Layer panel - moved to the left side */}
        {isLayerPanelOpen && (
          <LayerPanel 
            elements={elements}
            selectedElement={selectedElement}
            onSelect={setSelectedElement}
            onMoveForward={moveElementForward}
            onMoveBackward={moveElementBackward}
            onBringToFront={bringElementToFront}
            onSendToBack={sendElementToBack}
          />
        )}
        
        <div 
          className="main-content" 
          ref={mainContentRef}
          style={isLayerPanelOpen ? { marginLeft: '250px' } : {}}
        >
          <div className="canvas-container">
            <Canvas
              elements={elements}
              selectedElement={selectedElement}
              setSelectedElement={setSelectedElement}
              updateElement={updateElement}
              deleteElement={deleteElement}
              isPreviewMode={isPreviewMode}
              addElement={addElement}
              getDefaultStyle={getDefaultStyle}
              deviceView={deviceView}
              eventId={eventId}
              invitationId={invitationId}
              initialElements={initialElements}
              onThumbnailGenerated={onElementsChange ? (thumbnail) => {
                // Pass the thumbnail back to EditorPage via the callback
                console.log("Generated thumbnail, forwarding to parent");
                if (onElementsChange.onThumbnailGenerated) {
                  onElementsChange.onThumbnailGenerated(thumbnail);
                }
              } : undefined}
            />
          </div>
        </div>
        
        {selectedElement && !isPreviewMode && (
          <ElementProperties
            element={elements.find(el => el.id === selectedElement)}
            updateElement={updateElement}
            invitationId={invitationId}
          />
        )}
      </div>
    </div>
  );
}

// Helper functions
function getDefaultContent(type) {
  switch(type) {
    case 'text': return 'Edit this text';
    case 'heading': return 'Heading';
    case 'button': return 'Click Me';
    default: return '';
  }
}

function getDefaultStyle(type) {
  // Use elementRegistry's defaultStyles if available
  if (elementRegistry[type]?.defaultStyles) {
    return { ...elementRegistry[type].defaultStyles };
  }

  // Fallback default styling based on element type
  const baseStyle = { 
    width: '300px',
    height: 'auto'
  };
  
  switch(type) {
    case 'text': 
      return { 
        ...baseStyle,
        fontSize: '16px',
        color: '#333',
        fontFamily: 'Arial',
        lineHeight: '1.5',
        letterSpacing: '0px',
        textAlign: 'left'
      };
    case 'image':
      return {
        ...baseStyle,
        objectFit: 'contain'
      };
    case 'video':
      return {
        ...baseStyle,
        width: '320px',
        height: '240px'
      };
    case 'shape':
      return {
        ...baseStyle,
        width: '150px',
        height: '150px',
        fill: '#3f51b5',
        opacity: 1,
        boxShadow: 'none'
      };
    case 'rsvp-button':
      return {
        ...baseStyle,
        width: '200px',
        height: '50px',
        backgroundColor: '#4caf50',
        textColor: '#ffffff',
        text: 'RSVP Now',
        borderRadius: '4px',
        fontSize: '16px',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      };
    case 'rsvp-form':
      return {
        ...baseStyle,
        width: '400px',
        height: 'auto',
        backgroundColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      };
    case 'background':
      return {
        width: '250px',
        height: '75px',
        backgroundColor: '#ffffff',
        backgroundImage: '',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: -10000,
        opacity: 1
      };
    default: 
      return baseStyle;
  }
}

// Export both as named and default export for maximum compatibility
export { App };
export default App;

console.log("===== App.jsx exports have been defined ====="); 